import React, { useState, useCallback } from 'react'; // Dodano useCallback
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Loader2, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import './PublicAiOrderForm.css';

// Stałe dla odpytywania
const POLLING_INTERVAL = 5000; // 5 sekund
const MAX_POLLING_ATTEMPTS = 36; // 5s * 36 = 180s = 3 minuty

function PublicAiOrderForm({ backendUrl }) {
  const { docnumber } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [projectInfo, setProjectInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderPublicKey, setOrderPublicKey] = useState(null);
  const [pollingMessage, setPollingMessage] = useState(''); // Stan dla komunikatu odpytywania

  // Funkcja do odpytywania statusu (podobna do tej w useOrderDetailsActions)
  const pollInteractionStatus = useCallback(async (interactionId) => {
    let attempts = 0;
    const poll = async (resolve, reject) => {
      if (attempts >= MAX_POLLING_ATTEMPTS) {
        reject(new Error("Przekroczono limit czasu oczekiwania na odpowiedź serwera dotyczącą zapisu projektu."));
        return;
      }
      attempts++;
      setPollingMessage(`Sprawdzanie statusu (${attempts}/${MAX_POLLING_ATTEMPTS})...`); // Aktualizuj komunikat
      try {
        // Używamy nowego publicznego endpointu
        const statusUrl = `${backendUrl}/public/ai-interaction-status/${interactionId}`; 
        const statusRes = await fetch(statusUrl); // Nie potrzeba credentials: 'include' dla publicznej trasy
        
        if (!statusRes.ok) {
          if (statusRes.status !== 404) {
            const errorText = await statusRes.text();
            console.error(`[pollInteractionStatus - Public] Błąd HTTP ${statusRes.status} podczas odpytywania: ${errorText}`);
          }
          setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
          return;
        }
        
        const statusData = await statusRes.json();
        console.log(`[pollInteractionStatus - Public] Attempt ${attempts}, Status for ${interactionId}:`, statusData);

        if (statusData.status === 'completed_success') {
          // Zwracamy również orderPublicKey, jeśli jest dostępny w danych statusu
          resolve({ 
            success: true, 
            message: statusData.message || "Projekt został pomyślnie zapisany.", 
            interactionId: interactionId,
            orderPublicKey: statusData.data?.orderPublicKey // Odczytaj publicKey z pola data
          });
        } else if (statusData.status === 'completed_error') {
          reject(new Error(statusData.message || "Wystąpił błąd po stronie serwera podczas przetwarzania AI."));
        } else if (statusData.status === 'pending' || statusData.status === 'not_found') {
          setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
        } else {
          reject(new Error(`Nieznany status interakcji: ${statusData.status}`));
        }
      } catch (pollError) {
        console.error("[pollInteractionStatus - Public] Błąd podczas odpytywania:", pollError);
        setTimeout(() => poll(resolve, reject), POLLING_INTERVAL);
      }
    };
    return new Promise(poll);
  }, [backendUrl]); // Dodano backendUrl jako zależność

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setPollingMessage(''); // Resetuj komunikat odpytywania

    // Prosta walidacja
    if (!email || !email.includes('@')) {
      setError('Proszę podać poprawny adres email.');
      return;
    }
    if (!projectInfo.trim()) {
      setError('Proszę podać informacje dotyczące projektu.');
      return;
    }

    setIsLoading(true);
    const clientInteractionId = `client-public-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    try {
      const response = await fetch(`${backendUrl}/public/ai-order-from-template/${docnumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientEmail: email,
          projectInfo: projectInfo,
          clientInteractionId: clientInteractionId // Wyślij ID interakcji
        }),
      });

      // Sprawdź status odpowiedzi - jeśli 524 lub inny błąd, rozpocznij odpytywanie
      if (!response.ok) {
         // Spróbuj odczytać błąd JSON, jeśli się nie uda, użyj statusu
         let errorMessage = `Błąd serwera (status ${response.status})`;
         let interactionIdFromError = clientInteractionId; // Domyślnie użyj wygenerowanego
         try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
            interactionIdFromError = errorData.interactionId || clientInteractionId; // Użyj ID z błędu, jeśli jest
         } catch (parseError) { /* Ignoruj błąd parsowania */ }

         // Jeśli to błąd timeout lub inny błąd serwera, rozpocznij odpytywanie
         if (response.status === 524 || response.status === 504 || response.status >= 500) {
            console.warn(`[handleSubmit - Public] Otrzymano status ${response.status}. Rozpoczynanie odpytywania dla interactionId: ${interactionIdFromError}`);
            setPollingMessage('Serwer przetwarza żądanie. Sprawdzanie statusu...'); // Pokaż komunikat o odpytywaniu
            
            const pollingResult = await pollInteractionStatus(interactionIdFromError);
            
            // Sukces odpytywania
            setSuccess(true);
            setOrderPublicKey(pollingResult.orderPublicKey); // Ustaw publicKey z wyniku odpytywania
            setPollingMessage(''); // Wyczyść komunikat odpytywania
            setIsLoading(false); // Zakończ ładowanie
            // Nie kończymy funkcji tutaj, aby reszta kodu mogła się wykonać (np. wyświetlenie guzika)
            // return; 
         } else {
            // Inny błąd klienta lub serwera - rzuć błąd, aby został złapany poniżej
            throw new Error(errorMessage);
         }
      }

      // Jeśli odpowiedź była OK (np. 200)
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Wystąpił błąd podczas generowania zamówienia.');
      }

      // Sukces - bezpośrednia odpowiedź
      setSuccess(true);
      setOrderPublicKey(data.orderPublicKey); 
      setIsLoading(false);

    } catch (err) {
      console.error("Błąd podczas wysyłania formularza AI:", err);
      setError(err.message || 'Nie udało się połączyć z serwerem.');
      setIsLoading(false);
      setPollingMessage(''); // Wyczyść komunikat odpytywania w razie błędu
    }
  };

  return (
    <div className="ai-order-form-container">
      <h1>W ciągu 5 min przygotujemy dla Ciebie projekt</h1>
      {/* Usunięto podtytuł */}

      {isLoading ? (
        <div className="ai-order-message info">
          <Loader2 size={20} className="animate-spin" />
          <div>
            <span>Tworzymy projekt dla Ciebie</span>
            {/* Wyświetlaj komunikat o odpytywaniu, jeśli istnieje */}
            {pollingMessage ? (
                 <p className="small-info">{pollingMessage}</p>
            ) : (
                 <p className="small-info">Możesz bezpiecznie zamknąć tę stronę. Link do gotowego projektu wyślemy na podany adres e-mail ({email}). Jeśli wolisz poczekać, za chwilę pojawi się tutaj przycisk do przejścia do panelu zamówienia.</p>
            )}
          </div>
        </div>
      ) : success ? (
        <div className="ai-order-message success">
          {/* Usunięto logowanie stanu */}
          <CheckCircle size={20} />
          <div>
            <span>Projekt został wygenerowany pomyślnie!</span>
            <p className="small-info">Link do panelu zamówienia został również wysłany na Twój adres e-mail ({email}). Możesz teraz przejść do panelu, aby zobaczyć i edytować swój projekt.</p>
            {orderPublicKey && (
              <button
                onClick={() => navigate(`/client/orders/${orderPublicKey}`)} // Poprawiono ścieżkę na 'orders'
                className="client-btn" // Zastosowano klasę .client-btn
              >
                <ExternalLink size={18} /> {/* Ikona pozostaje */}
                Przejdź do panelu zamówienia
              </button>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="ai-order-form">
           {/* Komunikat o błędzie przeniesiony nad pola formularza dla lepszej widoczności */}
           {error && (
            <div className="ai-order-message error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">Twój adres email:</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="np. jan.kowalski@example.com"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="projectInfo">Informacje do projektu:</label>
            <textarea
              id="projectInfo"
              value={projectInfo}
              onChange={(e) => setProjectInfo(e.target.value)}
              required
              rows={8}
              placeholder="Podaj nam jak najwięcej danych, np. Imiona Pary Młodej, data, miejsce, godzina lub jaki napis ma się znaleźć na projekcie."
              disabled={isLoading}
            />
          </div>

          {/* Usunięto komunikat o błędzie stąd, przeniesiony wyżej */}

          <button type="submit" disabled={isLoading} className="submit-button">
            {/* Stan ładowania jest teraz obsługiwany przez blok warunkowy wyżej */}
            <>
              <Send size={18} />
              <span>Stwórz projekt i wyślij mi link</span>
              </>
          </button>
        </form>
      )}
    </div>
  );
}

export default PublicAiOrderForm;
